import { useContext, useEffect, useState } from 'react';
import { useForm, useFieldArray, Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    Box, Breadcrumbs, Typography, Switch, TextField, FormControlLabel,
    FormControl, InputLabel, Select, MenuItem, FormHelperText, OutlinedInput,
    InputAdornment, IconButton, Button, Grid
} from '@mui/material';
import dayjs from '../../../../config/dayjs';

import { getAll as getAllDrives } from '../../../../services/driveService';
import { getAll as getAllCourses } from '../../../../services/courseService';
import { getAll as getAllBatches } from '../../../../services/batchService';
import { createUser, getById, updateById } from '../../../../services/usersService';
import { getExamDates, getAll, getExamHalls, getServerIds } from '../../../../services/masterService';
import { useToaster } from '../../../../providers/ToasterProvider';

import { Container, MainContainer } from '../../../../components';
import { Menu } from '../Menu/Menu';
import RadiusButton from '../../../../components/RadiusButton';
import { UserCreationMail } from '../UserCreationMail/UserCreationMail';

import ARROW from '../../../../assets/images/breadcrumb-arrow.svg';
import ARROW_OPEN from '../../../../assets/images/breadcrumb-arrow-open.svg';
import EYE_ICON from '../../../../assets/images/eye.svg';
import EYE_CLOSE_ICON from '../../../../assets/images/eye-close.svg'
import BACK from '../../../../assets/images/back.svg';

import { LoadingButton } from '@mui/lab';
import { ProjectContext } from '../../../../providers/projectProvider';
import patterns from '../../../../constant/patterns';

const EMPTY_EXAM_ROW = { examDate: '', drive: 0, course: 0, batch: 0, examHall: 0, server: 0 };

// Exam dates are shown as DD-MM-YYYY; the API expects MM-DD-YYYY.
const toApiExamDate = (examDate) => `${examDate.substring(3, 5)}-${examDate.substring(0, 2)}-${examDate.substring(6, 10)}`;

// Drive + course + batch + date is one exam; the same exam twice for one user is not allowed.
const hasDuplicateExam = (examRows) => {
    const examKeys = examRows.map(({ drive, course, batch, examDate }) => `${drive}|${course}|${batch}|${examDate}`);
    return new Set(examKeys).size !== examKeys.length;
};

export const BiometricDetails = () => {

    const params = useParams();
    const projectName = params?.projectName;
    const location = useLocation();
    const navigate = useNavigate();
    const { addNotification } = useToaster();
    const { projectDetails } = useContext(ProjectContext);

    const [isActive, setIsActive] = useState(true);
    const [drives, setDrives] = useState([]);
    const [allCourses, setAllCourses] = useState([]);
    const [courses, setCourses] = useState({});
    const [allBatches, setAllBatches] = useState([]);
    const [batches, setBatches] = useState({});
    const [allProjectMasters, setAllProjectMasters] = useState([]);
    const [examDates, setExamDates] = useState({});
    const [examHalls, setExamHalls] = useState({});
    const [servers, setServers] = useState({});
    const [userDetails, setUserDetails] = useState(null);
    const [mailModal, setMailModal] = useState({
        isOpen: false,
        isUpdated: false,
        cancelPressed: false,
    });
    const [editMode, setEditMode] = useState(false);
    const [passwordVisible, setPasswordVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const biometricSchema = z.object({
        firstName: z.string().min(1, { message: 'First name is required.' }),
        lastName: z.string().min(1, { message: 'Last name is required.' }),
        username: z.string().min(1, { message: 'User name is required.' }),
        email: z.string().min(1, { message: 'User id is required.' }).email('Please enter valid email address.'),
        password: z.string(),
        courseBatches: z.array(
            z.object({
                drive: z.number().gte(1, { message: "Drive is required" }),
                course: z.number().gte(1, { message: "Course is required" }),
                batch: z.number().gte(1, { message: "Batch is required" }),
                examDate: z.string().min(1, { message: "Please select exam date." }),
                examHall: z.number().gte(1, { message: "Exam hall is required" }),
                server: z.number().gte(1, { message: "Server is required" }),
            })
        ).min(1, { message: "At least one Course + Batch is required" })

    }).superRefine(({ username, password }, ctx) => {
        if ((editMode && username && (username.length < 8 || username.length > 20)) || (!editMode && (username.length < 8 || username.length > 20))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username should be at least 8 characters long and shorter than 20 characters',
                path: ['username']
            });
        }
        if ((editMode && username && !patterns.USERNAME.test(username)) || (!editMode && !patterns.USERNAME.test(username))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Username must contain only alphanumeric characters, underscores, periods, or hyphens',
                path: ['username']
            });
        }
        if ((editMode && password && password.length < 8) || (!editMode && password.length < 8)) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password should be at least 8 characters long',
                path: ['password']
            });
        }
        if ((editMode && password && !patterns.PASSWORD.test(password)) || (!editMode && !patterns.PASSWORD.test(password))) {
            ctx.addIssue({
                code: 'custom',
                message: 'Password must contain one uppercase character, one lowercase character, one digit and one special character',
                path: ['password']
            });
        }
    });

    const {
        register,
        handleSubmit,
        formState: { errors },
        trigger,
        control,
        reset,
    } = useForm({
        resolver: zodResolver(biometricSchema),
        defaultValues: {
            courseBatches: [{ ...EMPTY_EXAM_ROW }]
        }
    });

    const { fields, append, remove } = useFieldArray({
        control,
        name: "courseBatches"
    });

    const watchedRows = useWatch({
        control,
        name: "courseBatches"
    });

    useEffect(() => {
        const fetchRequiredData = async () => {
            try {
                let localCourses = [], localBatches = [], localProjectMasters = [];

                const driveRes = await getAllDrives();
                if (driveRes.code === 'OK') setDrives([...driveRes.data]);

                const courseRes = await getAllCourses();
                if (courseRes.code === 'OK') {
                    localCourses = courseRes.data;
                    setAllCourses([...courseRes.data]);
                }

                const batchRes = await getAllBatches();
                if (batchRes.code === 'OK') {
                    localBatches = batchRes.data;
                    setAllBatches([...batchRes.data]);
                }

                const pmRes = await getAll();
                if (pmRes.code === 'OK') {
                    localProjectMasters = pmRes.data.list;
                    setAllProjectMasters([...pmRes.data.list]);
                }

                const userId = location?.state?.userId;
                if (!userId) return;

                try {
                    const res = await getById(userId);
                    if (res.code === 'OK') {
                        const details = res.data;

                        setEditMode(true);
                        setIsActive(details.isActive);
                        reset({
                            firstName: details.firstName || '',
                            lastName: details.lastName || '',
                            username: details.username || '',
                            email: details.email || '',
                            password: '',
                            courseBatches: details.assignments?.length
                                ? details.assignments.map(item => ({
                                    drive: item.driveId || 0,
                                    course: item.courseId || 0,
                                    batch: item.batchId || 0,
                                    examDate: item.examDate ? dayjs(item.examDate).format('DD-MM-YYYY') : '',
                                    examHall: item.examHallId || 0,
                                    server: item.serverId || 0
                                }))
                                : [{ ...EMPTY_EXAM_ROW }]
                        });

                        // Local variables, not state: the state setters above have not applied yet.
                        details.assignments?.forEach((item, index) => {
                            const filteredCourses = localProjectMasters
                                .filter(pm => pm.driveId === item.driveId)
                                .map(pm => localCourses.find(c => c.id === pm.courseId))
                                .filter(Boolean);
                            setCourses(prev => ({
                                ...prev,
                                [index]: [...new Map(filteredCourses.map(c => [c.id, c])).values()]
                            }));

                            const filteredBatches = localProjectMasters
                                .filter(pm => pm.driveId === item.driveId && pm.courseId === item.courseId)
                                .map(pm => localBatches.find(b => b.id === pm.batchId))
                                .filter(Boolean);
                            setBatches(prev => ({
                                ...prev,
                                [index]: [...new Map(filteredBatches.map(b => [b.id, b])).values()]
                            }));
                        });

                        setUserDetails(details);
                    }
                } catch (error) {
                    console.log('There is an error while getting biometric user details', error);
                }
            } catch (error) {
                console.log('There is an error while fetching required data', error);
            }
        }
        fetchRequiredData();

    }, [location]);

    useEffect(() => {
        if (mailModal.cancelPressed) {
            const path = location.pathname.replace('/biometric-details', '');
            navigate(path);
        }
    }, [mailModal, navigate, location]);

    useEffect(() => {
        if (!watchedRows) return;
        watchedRows.forEach(async (row, index) => {
            const { drive, course, batch, examDate, examHall } = row;
            const examDateForApi = examDate ? toApiExamDate(examDate) : '';

            if (drive && drive !== 0) {
                const filteredCourses = allProjectMasters
                    .filter(pm => pm.driveId === drive)
                    .map(pm => allCourses.find(c => c.id === pm.courseId))
                    .filter(Boolean);

                setCourses(prev => ({
                    ...prev,
                    [index]: [...new Map(filteredCourses.map(c => [c.id, c])).values()]
                }));
            }

            if (drive && course) {
                const filteredBatches = allProjectMasters
                    .filter(pm => pm.driveId === drive && pm.courseId === course)
                    .map(pm => allBatches.find(b => b.id === pm.batchId))
                    .filter(Boolean);

                setBatches(prev => ({
                    ...prev,
                    [index]: [...new Map(filteredBatches.map(b => [b.id, b])).values()]
                }));
            }

            try {
                if (drive && course && batch) {
                    const res = await getExamDates({ driveId: drive, courseId: course, batchId: batch });
                    if (res.code === "OK") {
                        const dates = res.data.projectMasters.map(pm =>
                            dayjs(pm.examDate).format("DD-MM-YYYY")
                        );
                        setExamDates(prev => ({ ...prev, [index]: dates }));
                    }
                }

                if (drive && course && batch && examDate) {
                    const res = await getExamHalls({
                        driveId: drive,
                        courseId: course,
                        batchId: batch,
                        examDate: examDateForApi
                    });
                    if (res.code === "OK") {
                        setExamHalls(prev => ({ ...prev, [index]: res.data.list ?? [] }));
                    }
                }

                if (drive && course && batch && examDate && examHall) {
                    const res = await getServerIds({
                        driveId: drive,
                        courseId: course,
                        batchId: batch,
                        examDate: examDateForApi,
                        examHallId: examHall
                    });
                    if (res.code === "OK") {
                        setServers(prev => ({ ...prev, [index]: res.data.list ?? [] }));
                    }
                }
            } catch (error) {
                console.log('There is an error while fetching exam options', error);
            }
        });

    }, [watchedRows]);

    const onGoBack = () => {
        if (projectName) {
            navigate(`/${projectName}/biometric`);
        }
    }

    const submitForm = async (values) => {
        if (!values.courseBatches?.length) {
            return;
        }

        const hasInvalidRow = values.courseBatches.some(row =>
            !row.drive ||
            !row.course ||
            !row.batch ||
            !row.examDate ||
            !row.server ||
            !row.examHall
        );
        if (hasInvalidRow) {
            addNotification('Please complete all Course + Batch rows.', 'warning');
            return;
        }

        if (hasDuplicateExam(values.courseBatches)) {
            addNotification('This exam is already assigned to this user. Remove the repeated Drive / Course / Batch / Exam Date row.', 'warning');
            return;
        }

        const assignments = values.courseBatches.map(row => ({
            driveId: row.drive,
            courseId: row.course,
            batchId: row.batch,
            examDate: toApiExamDate(row.examDate),
            serverId: row.server,
            examHallId: row.examHall
        }));

        const body = {
            firstName: values.firstName,
            lastName: values.lastName,
            username: values.username,
            email: values.email,
            password: values.password,
            isActive,
            assignments
        };

        setIsLoading(true);
        try {
            if (editMode) {
                if (!body.password) {
                    delete body.password;
                }
                const res = await updateById(userDetails.id, body);
                if (res.code === 'OK') {
                    addNotification('User details updated successfully', 'success');
                    if (body.password) {
                        setUserDetails({
                            ...res.data.user,
                            password: values.password
                        });
                        setMailModal({
                            ...mailModal,
                            isUpdated: true,
                            isOpen: true
                        });
                    } else {
                        onGoBack();
                    }
                }
            } else {
                body.userType = 5;
                const res = await createUser(body);
                if (res.code === 'CREATED') {
                    const data = {
                        ...res.data.user,
                        password: values.password
                    }
                    setUserDetails(data);
                    setMailModal({
                        ...mailModal,
                        isOpen: true,
                    });
                    addNotification('User added successfully', 'success');
                }
            }
        } catch (error) {
            if (error?.response?.data?.data?.code === 'E_DUPLICATE') {
                addNotification('User name is taken.', 'danger');
            } else if (error?.response?.data?.code === 'EXAM_ALREADY_ASSIGNED') {
                addNotification('This exam is already assigned to this user.', 'danger');
            }
            console.log('There is an error while saving biometric user', error);
        } finally {
            setIsLoading(false);
        }
    }

    const handleActiveChange = () => {
        setIsActive(!isActive)
    }

    return (
        <MainContainer sx={{ bgcolor: 'red' }}>
            <Menu>
                <Breadcrumbs aria-label="breadcrumb" separator='' sx={{ gap: 12 }}>
                    <Typography color="text.primary" sx={{
                        fontWeight: 400,
                        textTransform: 'capitalize'
                    }}>{projectDetails?.projectName}</Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW} alt=''></img>
                    </Box>

                    <Typography color="text.primary" sx={{ fontWeight: 500 }}>
                        Biometric
                    </Typography>
                    <Box sx={{ display: 'flex', alignContent: 'center' }}>
                        <img src={ARROW_OPEN} alt='' ></img>
                    </Box>
                </Breadcrumbs>
            </Menu>
            <Container sx={{
                display: 'flex',
                flexDirection: 'column',
            }}>

                <div className="search-header-wrapper" style={{ marginBottom: '24px' }}>
                    <Typography component='h5' variant='h5' sx={{
                        mr: 'auto',
                    }}>
                        {editMode ? 'Edit' : 'Create'} Biometric User
                    </Typography>
                </div>

                <Box sx={{
                    bgcolor: 'background.base',
                    borderRadius: 6,
                    p: 4
                }} className='create-user-form-wrapper'>
                    <Box component='form' autoComplete='off' onSubmit={handleSubmit(submitForm)}>
                        <div className="user-form-details">

                            <TextField
                                id='firstName'
                                name='firstName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>First Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('firstName')}
                                error={Boolean(errors.firstName)}
                                helperText={errors.firstName?.message}
                                onBlur={() => trigger('firstName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='lastName'
                                name='lastName'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Last Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('lastName')}
                                error={Boolean(errors.lastName)}
                                helperText={errors.lastName?.message}
                                onBlur={() => trigger('lastName')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                            />

                            <TextField
                                id='username'
                                name='username'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>User Name</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('username')}
                                error={Boolean(errors.username)}
                                helperText={errors.username?.message}
                                onBlur={() => trigger('username')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column'
                            }}>
                                <FormControl variant='outlined' sx={{ mb: 2 }}>
                                    <InputLabel htmlFor="passwordLabel">
                                        <Box component='span'>Password</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </InputLabel>
                                    <OutlinedInput
                                        id='passwordLabel'
                                        type={passwordVisible ? 'text' : 'password'}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    aria-label="toggle password visibility"
                                                    onClick={() => setPasswordVisible(!passwordVisible)}
                                                    edge="end"
                                                >
                                                    <img className='password-eye' src={passwordVisible ? EYE_CLOSE_ICON : EYE_ICON} alt='' />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label="Password"
                                        name="password"
                                        {...register('password')}
                                        error={Boolean(errors.password)}
                                        onBlur={() => trigger('password')}
                                    />
                                    {errors?.password?.message && (
                                        <FormHelperText sx={{ color: 'error.main' }}>
                                            {errors.password?.message}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                                {editMode && <FormHelperText sx={{ mt: '-10px', color: 'primary.main', mb: 1.5 }}>
                                    Enter password only if you wish to update.
                                </FormHelperText>}
                            </Box>

                            <TextField
                                id='email'
                                name='email'
                                className='form-field'
                                label={
                                    <>
                                        <Box component='span'>Email</Box>
                                        <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                    </>
                                }
                                variant='outlined'
                                {...register('email')}
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                                onBlur={() => trigger('email')}
                                InputLabelProps={editMode ? { shrink: true } : {}}
                                disabled={editMode ? true : false}
                            />

                            <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {fields.map((item, index) => (
                                    <Box
                                        key={item.id}
                                        sx={{
                                            border: '1px solid #ddd',
                                            borderRadius: 2,
                                            p: 2,
                                        }}
                                    >
                                        <Grid container spacing={2}>
                                            {/* Drive Name */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.drive`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.drive)}>
                                                            <InputLabel>
                                                                Drive Name
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Drive Name *">
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {drives?.map(drive => (
                                                                    <MenuItem key={drive.id} value={drive.id}>
                                                                        {drive.driveName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.drive && (
                                                                <FormHelperText>{errors.courseBatches[index].drive.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {/* Course Name */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.course`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.course)}>
                                                            <InputLabel>
                                                                Course Name
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Course Name *">
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {courses[index]?.map(course => (
                                                                    <MenuItem key={course.id} value={course.id}>
                                                                        {course.courseName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.course && (
                                                                <FormHelperText>{errors.courseBatches[index].course.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {/* Batch Name */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.batch`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.batch)}>
                                                            <InputLabel>
                                                                Batch Name
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Batch Name *">
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {batches[index]?.map(batch => (
                                                                    <MenuItem key={batch.id} value={batch.id}>
                                                                        {batch.batchName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.batch && (
                                                                <FormHelperText>{errors.courseBatches[index].batch.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {/* Exam Date */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.examDate`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.examDate)}>
                                                            <InputLabel>
                                                                Exam Date
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Exam Date *">
                                                                <MenuItem value="">None</MenuItem>
                                                                {examDates[index]?.map(date => (
                                                                    <MenuItem key={date} value={date}>
                                                                        {date}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.examDate && (
                                                                <FormHelperText>{errors.courseBatches[index].examDate.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {/* Exam Hall Name */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.examHall`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.examHall)}>
                                                            <InputLabel>
                                                                Exam Hall Name
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Exam Hall Name *">
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {examHalls[index]?.map(hall => (
                                                                    <MenuItem key={hall.id} value={hall.id}>
                                                                        {hall.hallName}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.examHall && (
                                                                <FormHelperText>{errors.courseBatches[index].examHall.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {/* Server Id */}
                                            <Grid item xs={12} sm={6}>
                                                <Controller
                                                    name={`courseBatches.${index}.server`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <FormControl fullWidth error={Boolean(errors.courseBatches?.[index]?.server)}>
                                                            <InputLabel>
                                                                Server Id
                                                                <Box component='span' sx={{ color: 'error.main', ml: 0.25 }}>*</Box>
                                                            </InputLabel>
                                                            <Select {...field} label="Server Id *">
                                                                <MenuItem value={0}>None</MenuItem>
                                                                {servers[index]?.map(server => (
                                                                    <MenuItem key={server.id} value={server.id}>
                                                                        {server.serverId}
                                                                    </MenuItem>
                                                                ))}
                                                            </Select>
                                                            {errors.courseBatches?.[index]?.server && (
                                                                <FormHelperText>{errors.courseBatches[index].server.message}</FormHelperText>
                                                            )}
                                                        </FormControl>
                                                    )}
                                                />
                                            </Grid>

                                            {fields.length > 1 && (
                                                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                    <Button
                                                        variant="outlined"
                                                        color="error"
                                                        size="small"
                                                        onClick={() => remove(index)}
                                                    >
                                                        Remove
                                                    </Button>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </Box>
                                ))}

                                <Box>
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        onClick={() => append({ ...EMPTY_EXAM_ROW })}
                                    >
                                        + Add Drive / Course / Batch
                                    </Button>
                                </Box>
                            </Box>
                        </div>

                        <FormControlLabel sx={{
                            mb: 3
                        }} control={
                            <Switch
                                checked={isActive}
                                onChange={handleActiveChange}
                            />
                        } label={<Box component='span'>Active</Box>} />

                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                        }}>
                            <RadiusButton sx={{
                                textTransform: 'uppercase',
                                fontWeight: 500,
                            }} color='grey'
                                variant='contained'
                                size='large'
                                onClick={onGoBack}
                                startIcon={<img src={BACK} alt='' />}>
                                Back
                            </RadiusButton>
                            <LoadingButton sx={{
                                textTransform: 'uppercase',
                                borderRadius: 40
                            }} color='primary'
                                type="submit"
                                variant='contained'
                                size='large'
                                loading={isLoading}
                            >
                                Submit
                            </LoadingButton>
                        </Box>

                    </Box>
                </Box>

            </Container>
            <UserCreationMail userDetails={userDetails} openState={mailModal.isOpen} isUpdated={mailModal.isUpdated} setMailModal={setMailModal} />
        </MainContainer >
    );
}
